// Updated workout-api.js with corrected getAthleteWeekSchedule function

import { db } from './firebase-config';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';

// Workout Templates
export const getCoachTemplates = async (coachId) => {
  try {
    if (!coachId) {
      console.error('No coach ID provided');
      return [];
    }
    
    const q = query(
      collection(db, 'workout_templates'),
      where('coach_id', '==', coachId),
      orderBy('created_at', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching templates:', error);
    return [];
  }
};

export const createWorkoutTemplate = async (coachId, templateData) => {
  try {
    if (!coachId || !templateData) {
      throw new Error('Missing required data');
    }

    const docRef = await addDoc(collection(db, 'workout_templates'), {
      ...templateData,
      coach_id: coachId,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating template:', error);
    return { success: false, error: error.message };
  }
};

export const updateWorkoutTemplate = async (templateId, templateData) => {
  try {
    if (!templateId || !templateData) {
      throw new Error('Missing required data');
    }

    await updateDoc(doc(db, 'workout_templates', templateId), {
      ...templateData,
      updated_at: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating template:', error);
    return { success: false, error: error.message };
  }
};

export const deleteWorkoutTemplate = async (templateId) => {
  try {
    if (!templateId) {
      throw new Error('No template ID provided');
    }

    await deleteDoc(doc(db, 'workout_templates', templateId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting template:', error);
    return { success: false, error: error.message };
  }
};

// Athletes
export const getCoachAthletes = async (coachId) => {
  try {
    if (!coachId) {
      console.error('No coach ID provided');
      return [];
    }
    
    const q = query(
  collection(db, 'users'),
  where('coach_id', '==', coachId),
  where('role', '==', 'athlete')
  
);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching athletes:', error);
    return [];
  }
};

// Feedback
export const getRecentFeedback = async (coachId, limit = 10) => {
  try {
    if (!coachId) {
      console.error('No coach ID provided');
      return [];
    }
    
    const q = query(
      collection(db, 'workout_feedback'),
      where('coach_id', '==', coachId),
      orderBy('completed_at', 'desc')
    );
    const snapshot = await getDocs(q);
    
    const feedback = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return feedback.slice(0, limit);
  } catch (error) {
    console.error('Error fetching feedback:', error);
    return [];
  }
};

// Weekly Schedules - CORRECTED VERSION TO READ FROM WEEKLY_SCHEDULES
export const getAthleteWeekSchedule = async (athleteId, weekStart, weekEnd) => {
  try {
    // Input validation
    if (!athleteId) {
      console.error('No athlete ID provided');
      return null;
    }
    if (!weekStart || !weekEnd) {
      console.error('Invalid date range provided');
      return null;
    }

    // Convert dates to proper format
    const startDate = weekStart instanceof Date ? weekStart : new Date(weekStart);
    const endDate = weekEnd instanceof Date ? weekEnd : new Date(weekEnd);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error('Invalid dates provided');
      return null;
    }

    console.log('Fetching schedule for:', { athleteId, startDate, endDate });

    // Query the weekly_schedules collection (where coach actually saves data)
    const q = query(
      collection(db, 'weekly_schedules'),
      where('athlete_id', '==', athleteId)
    );
    
    const snapshot = await getDocs(q);
    
    console.log(`Found ${snapshot.docs.length} weekly schedules for athlete`);
    
    if (!snapshot.empty) {
      // Find the schedule that matches our week
      for (const doc of snapshot.docs) {
        const scheduleData = doc.data();
        console.log('Processing schedule:', scheduleData);
        
        // Check if this schedule overlaps with our week
        const scheduleStart = scheduleData.week_start?.toDate ? scheduleData.week_start.toDate() : new Date(scheduleData.week_start);
        const scheduleEnd = scheduleData.week_end?.toDate ? scheduleData.week_end.toDate() : new Date(scheduleData.week_end);
        
        // Check if our week range overlaps with this schedule
        if (scheduleStart <= endDate && scheduleEnd >= startDate) {
          console.log('Found matching schedule:', scheduleData);
          
          return {
            id: doc.id,
            athlete_id: athleteId,
            week_start: startDate,
            week_end: endDate,
            workouts: scheduleData.workouts || {}
          };
        }
      }
    }

    // Return empty schedule structure if none exists
    console.log('No matching schedule found, returning empty structure');
    return {
      athlete_id: athleteId,
      week_start: startDate,
      week_end: endDate,
      workouts: {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null
      }
    };

  } catch (error) {
    console.error('Error fetching athlete schedule:', error);
    return null;
  }
};

export const saveWeeklySchedule = async (scheduleData) => {
  try {
    // Enhanced input validation
    if (!scheduleData) {
      throw new Error('No schedule data provided');
    }
    if (!scheduleData.athlete_id) {
      throw new Error('No athlete ID provided');
    }
    if (!scheduleData.week_start) {
      throw new Error('No week start date provided');
    }

    console.log('Saving schedule data:', scheduleData);

    // Convert dates to proper format
    const weekStart = scheduleData.week_start instanceof Date 
      ? scheduleData.week_start 
      : new Date(scheduleData.week_start);
    
    const weekEnd = scheduleData.week_end instanceof Date 
      ? scheduleData.week_end 
      : new Date(scheduleData.week_end);

    if (isNaN(weekStart.getTime())) {
      throw new Error('Invalid week start date');
    }

    // Prepare clean schedule data
    const cleanScheduleData = {
      athlete_id: scheduleData.athlete_id,
      week_start: weekStart,
      week_end: weekEnd || new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000),
      week_key: `${weekStart.getFullYear()}-W${Math.ceil(weekStart.getDate() / 7)}`,
      workouts: scheduleData.workouts || {},
      updated_at: serverTimestamp()
    };

    // Check if schedule already exists
    if (scheduleData.id) {
      // Update existing schedule
      console.log('Updating existing schedule:', scheduleData.id);
      await updateDoc(doc(db, 'weekly_schedules', scheduleData.id), cleanScheduleData);
      return { success: true, id: scheduleData.id };
    } else {
      // Create new schedule
      console.log('Creating new schedule');
      cleanScheduleData.created_at = serverTimestamp();
      
      const docRef = await addDoc(collection(db, 'weekly_schedules'), cleanScheduleData);
      return { success: true, id: docRef.id };
    }

  } catch (error) {
    console.error('Error saving weekly schedule:', error);
    return { 
      success: false, 
      error: `Failed to save schedule: ${error.message}` 
    };
  }
};

// Scheduled Workouts
export const getScheduledWorkouts = async (athleteId, date) => {
  try {
    if (!athleteId || !date) {
      console.error('Missing athleteId or date');
      return [];
    }

    const targetDate = date instanceof Date ? date : new Date(date);
    if (isNaN(targetDate.getTime())) {
      console.error('Invalid date provided');
      return [];
    }

    const q = query(
      collection(db, 'scheduled_workouts'),
      where('athlete_id', '==', athleteId),
      where('workout_date', '==', targetDate)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching scheduled workouts:', error);
    return [];
  }
};

export const createScheduledWorkout = async (workoutData) => {
  try {
    if (!workoutData || !workoutData.athlete_id || !workoutData.template_id) {
      throw new Error('Missing required workout data');
    }

    const docRef = await addDoc(collection(db, 'scheduled_workouts'), {
      ...workoutData,
      created_at: serverTimestamp(),
      completed: false,
      completion_date: null
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating scheduled workout:', error);
    return { success: false, error: error.message };
  }
};

export const updateScheduledWorkout = async (workoutId, updateData) => {
  try {
    if (!workoutId || !updateData) {
      throw new Error('Missing workout ID or update data');
    }

    await updateDoc(doc(db, 'scheduled_workouts', workoutId), {
      ...updateData,
      updated_at: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating scheduled workout:', error);
    return { success: false, error: error.message };
  }
};

export const deleteScheduledWorkout = async (workoutId) => {
  try {
    if (!workoutId) {
      throw new Error('No workout ID provided');
    }

    await deleteDoc(doc(db, 'scheduled_workouts', workoutId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting scheduled workout:', error);
    return { success: false, error: error.message };
  }
};

// Personal Bests
export const getPersonalBests = async (athleteId) => {
  try {
    if (!athleteId) {
      console.error('No athlete ID provided');
      return [];
    }
    
    const q = query(
      collection(db, 'workout_feedback'),
      where('athlete_id', '==', athleteId),
      where('is_personal_best', '==', true),
      orderBy('completed_at', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching personal bests:', error);
    return [];
  }
};

// Client Notes Updates
export const updateClientNotes = async (workoutId, notes) => {
  try {
    if (!workoutId) {
      throw new Error('No workout ID provided');
    }

    await updateDoc(doc(db, 'scheduled_workouts', workoutId), {
      client_notes: notes || '',
      updated_at: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating client notes:', error);
    return { success: false, error: error.message };
  }
};

// Week Overview and Week Switching
export const getWeekOverview = async (athleteId, weekStart, weekEnd) => {
  try {
    if (!athleteId || !weekStart || !weekEnd) {
      console.error('Missing required parameters');
      return null;
    }

    const startDate = weekStart instanceof Date ? weekStart : new Date(weekStart);
    const endDate = weekEnd instanceof Date ? weekEnd : new Date(weekEnd);

    const q = query(
      collection(db, 'scheduled_workouts'),
      where('athlete_id', '==', athleteId),
      where('workout_date', '>=', startDate),
      where('workout_date', '<=', endDate)
    );
    
    const snapshot = await getDocs(q);
    const workouts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      week_start: startDate,
      week_end: endDate,
      total_workouts: workouts.length,
      completed_workouts: workouts.filter(w => w.completed).length,
      workouts: workouts
    };
  } catch (error) {
    console.error('Error fetching week overview:', error);
    return null;
  }
};

// API Update Notes Function
export const apiUpdateNotes = async (workoutId, notes) => {
  return await updateClientNotes(workoutId, notes);
};

// Toggle Workout Completion
export const toggleWorkoutCompletion = async (workoutId, completed, athleteId) => {
  try {
    if (!workoutId) {
      throw new Error('No workout ID provided');
    }

    const updateData = {
      completed: completed,
      updated_at: serverTimestamp()
    };

    if (completed) {
      updateData.completion_date = serverTimestamp();
      updateData.completed_by = athleteId;
    } else {
      updateData.completion_date = null;
      updateData.completed_by = null;
    }

    await updateDoc(doc(db, 'scheduled_workouts', workoutId), updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error toggling workout completion:', error);
    return { success: false, error: error.message };
  }
};

// Toggle Personal Best
export const togglePersonalBest = async (workoutId, isPersonalBest, athleteId) => {
  try {
    if (!workoutId) {
      throw new Error('No workout ID provided');
    }

    const updateData = {
      is_personal_best: isPersonalBest,
      updated_at: serverTimestamp()
    };

    if (isPersonalBest) {
      updateData.personal_best_date = serverTimestamp();
      updateData.personal_best_by = athleteId;
    } else {
      updateData.personal_best_date = null;
      updateData.personal_best_by = null;
    }

    await updateDoc(doc(db, 'scheduled_workouts', workoutId), updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error toggling personal best:', error);
    return { success: false, error: error.message };
  }
};

// API Toggle Personal Best (alias)
export const apiTogglePersonalBest = async (workoutId, isPersonalBest, athleteId) => {
  return await togglePersonalBest(workoutId, isPersonalBest, athleteId);
};